import React, { ReactNode, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Tabs,
  Tab,
  useTheme,
  
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { RootState } from "src/redux-store/appStore";

const TabsWrapper = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-scrollableX': {
    overflowX: 'auto !important',
  },
  '& .MuiTab-root': {
    fontSize: '0.9rem',
    [theme.breakpoints.down('sm')]: {
      minWidth: 90,
      padding: '6px 8px',
    },
  },
}));

interface TabConfig {
  value: string;
  label: string;
  component: ReactNode;
}

interface ReportLayoutProps {
  title: string;
  tabs?: TabConfig[];
  children?: ReactNode;
}

const ReportLayout: React.FC<ReportLayoutProps> = ({ title, tabs, children }) => {
  const theme = useTheme();
  const userDetails = useSelector((store: RootState) => store.user.userDetails);

  const [currentTab, setCurrentTab] = useState<string>(tabs?.[0]?.value || '');

  useEffect(() => {
    if (!currentTab && tabs?.length) {
      setCurrentTab(tabs[0].value);
    }
  }, [tabs, currentTab]);

  const handleTabsChange = (_: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  };

  const renderContent = () => {
    if (tabs && tabs.length > 1) {
      const tab = tabs.find(t => t.value === currentTab) || tabs[0];
      return tab.component;
    } else if (children) {
      return children;
    } else if (tabs && tabs.length === 1) {
      return tabs[0].component;
    }
    return null;
  };

  return (
    <Box px={{ xs: 1, sm: 2, md: 3 }} pb={5}>
      <Box sx={{ py: 3, borderRadius: 2, boxShadow: 2 }}>
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: 1,
            boxShadow: 'rgba(17, 17, 26, 0.05) 0px 1px 0px, rgba(17, 17, 26, 0.1) 0px 0px 8px',
            p: { xs: 2, sm: 3 },
          }}
        >
          {/* Header */}
          <Box
            textAlign="center"
            sx={{
              backgroundColor: theme.palette.primary.main,
              borderRadius: '10px 10px 0 0',
              p: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="h4"
              color="white"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' },
              }}
            >
              {title}
            </Typography>
          </Box>

          {/* Summary Info */}
          <Paper sx={{ px: 2, py: 3, mb: 3, bgcolor: '#ecf0f9' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} textAlign="center">
                <Typography variant="body1" fontWeight={600}>
                  Township
                </Typography>
                <Typography variant="h6" mt={1}>
                  {userDetails.TownshipName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} textAlign="center">
                <Typography variant="body1" fontWeight={600}>
                  Report Date
                </Typography>
                <Typography variant="h6" mt={1}>
                  {new Date().toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                  })}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Tabs if multiple views */}
          {tabs && tabs.length > 1 && (
            <Box sx={{ mb: 3 }}>
              <TabsWrapper
                value={currentTab}
                onChange={handleTabsChange}
                variant="scrollable"
                scrollButtons="auto"
                textColor="primary"
                indicatorColor="primary"
              >
                {tabs.map(tab => (
                  <Tab key={tab.value} label={tab.label} value={tab.value} />
                ))}
              </TabsWrapper>
            </Box>
          )}

          {/* Content */}
          <Box mt={2}>{renderContent()}</Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ReportLayout;
